import { METRONOME } from '@/config/audio';

export interface BeatInfo {
  /** Beat counter since `start()`, zero-based. */
  index: number;
  /** Position inside the bar, zero-based. */
  beatInBar: number;
  /** Exact AudioContext time the click sounds at. */
  time: number;
  accent: boolean;
}

export interface MetronomeOptions {
  bpm: number;
  beatsPerBar: number;
  /** Play the click. Timing still runs when false — the UI keeps counting. */
  audible: boolean;
}

type BeatListener = (beat: BeatInfo) => void;

/**
 * Web Audio metronome using the two-clock pattern (Chris Wilson,
 * "A Tale of Two Clocks").
 *
 * A `setInterval` is far too coarse to place a click — it drifts and jitters by
 * tens of milliseconds. So the interval is used only as a *wake-up*: on each
 * wake it schedules every click that falls inside the next lookahead window
 * directly on the sample-accurate AudioContext clock.
 *
 * UI callbacks are queued with their exact audio time and fired from a
 * requestAnimationFrame loop as `currentTime` passes them, so the flashing beat
 * and the click never drift apart — which matters, because the player watches
 * the beat row while listening to the click.
 */
export class Metronome {
  private options: MetronomeOptions = { bpm: 60, beatsPerBar: 4, audible: true };
  private timer: ReturnType<typeof setInterval> | null = null;
  private rafId: number | null = null;
  private nextBeatIndex = 0;
  private nextBeatTime = 0;
  private startTime = 0;
  private running = false;
  private readonly queue: BeatInfo[] = [];
  private readonly listeners = new Set<BeatListener>();

  constructor(private readonly context: AudioContext) {}

  get isRunning(): boolean {
    return this.running;
  }

  get bpm(): number {
    return this.options.bpm;
  }

  /** Seconds per beat at the current tempo. */
  get beatInterval(): number {
    return 60 / this.options.bpm;
  }

  /** AudioContext time of the first beat of the current run. */
  get startedAt(): number {
    return this.startTime;
  }

  onBeat(listener: BeatListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  configure(patch: Partial<MetronomeOptions>): void {
    this.options = { ...this.options, ...patch };
  }

  /**
   * Change tempo without stopping. The start time is re-anchored so that
   * `timeOfBeat()` stays exact for every beat that has not been scheduled yet —
   * already-scheduled clicks keep the old spacing, which is correct: they are
   * committed to the audio clock and cannot be moved.
   */
  changeTempo(bpm: number): void {
    if (bpm === this.options.bpm) return;
    this.options = { ...this.options, bpm };
    if (!this.running) return;
    this.startTime = this.nextBeatTime - this.nextBeatIndex * this.beatInterval;
  }

  /**
   * Start counting. `delaySec` gives the player a moment before beat 0, which
   * also keeps the very first click clear of the click of the Start button.
   */
  start(delaySec = 0.12): void {
    if (this.running) return;
    this.running = true;
    this.nextBeatIndex = 0;
    this.startTime = this.context.currentTime + delaySec;
    this.nextBeatTime = this.startTime;
    this.queue.length = 0;
    this.timer = setInterval(() => this.schedule(), METRONOME.lookaheadMs);
    this.schedule();
    this.tick();
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.queue.length = 0;
  }

  /** Scheduled time of a beat index, whether or not it has happened yet. */
  timeOfBeat(index: number): number {
    return this.startTime + index * this.beatInterval;
  }

  private schedule(): void {
    if (!this.running) return;
    const horizon = this.context.currentTime + METRONOME.scheduleAheadSec;
    while (this.nextBeatTime < horizon) {
      const beatInBar = this.nextBeatIndex % this.options.beatsPerBar;
      const beat: BeatInfo = {
        index: this.nextBeatIndex,
        beatInBar,
        time: this.nextBeatTime,
        accent: beatInBar === 0,
      };
      if (this.options.audible) this.click(beat);
      this.queue.push(beat);

      this.nextBeatIndex++;
      // Recomputed from the anchor rather than accumulated, so long runs and
      // tempo changes cannot collect floating-point drift. `changeTempo`
      // re-derives the anchor from the next unscheduled beat, so this stays
      // exact across tempo changes too.
      this.nextBeatTime = this.startTime + this.nextBeatIndex * this.beatInterval;
    }
  }

  private click(beat: BeatInfo): void {
    const { click } = METRONOME;
    const osc = this.context.createOscillator();
    const gain = this.context.createGain();
    osc.frequency.value = beat.accent ? click.accentFrequency : click.normalFrequency;
    osc.type = 'square';

    const t = beat.time;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(beat.accent ? click.gain : click.gain * 0.7, t + click.attackSec);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + click.durationSec);

    osc.connect(gain);
    gain.connect(this.context.destination);
    osc.start(t);
    osc.stop(t + click.durationSec + 0.01);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
  }

  private tick = (): void => {
    if (!this.running) return;
    const now = this.context.currentTime;
    while (this.queue.length > 0 && this.queue[0].time <= now) {
      const beat = this.queue.shift();
      if (!beat) break;
      for (const listener of this.listeners) listener(beat);
    }
    this.rafId = requestAnimationFrame(this.tick);
  };
}
